import { UAParser } from "ua-parser-js";

/**
 * 解析結果
 */
interface DeviceInfo {
  os: string;
  osVersion: string;
  browser: string;
}

/**
 * 画面サイズ
 */
export interface ScreenSize {
  width: number;
  height: number;
}

/**
 * 端末サービス
 */
export class DeviceService {
  /**
   * iOS: ノッチありの画面サイズ
   */
  private readonly NOTCH_SIZES = [
    [828, 1792], // iPhone XR, iPhone 11
    [1125, 2436], // iPhone X, iPhone XS, iPhone 11 Pro
    [1242, 2688], // iPhone XS Max, iPhone 11 Pro Max
  ];

  /**
   * 解析したUA
   */
  private readonly userAgent: string;

  /**
   * 解析結果
   */
  private readonly data: DeviceInfo;

  /**
   * ノッチありか
   */
  private readonly notchDetected: boolean;

  /**
   * コンストラクタ
   */
  constructor(userAgent: string, screen: ScreenSize, ratio: number) {
    this.userAgent = userAgent;
    this.data = this.parse(userAgent);
    this.notchDetected = this.parseNotch(this.data.os, screen, ratio);
  }

  /**
   * OS名を取得する
   */
  public getOs(): string {
    return this.data.os;
  }

  /**
   * スマートフォンか
   */
  public isSp(): boolean {
    return ["ios", "android"].includes(this.getOs());
  }

  /**
   * ブラウザ名を取得する
   */
  public getBrowser(): string {
    return this.data.browser;
  }

  /**
   * IEか
   */
  public isIE(): boolean {
    return this.getBrowser() === "ie";
  }

  /**
   * iOSか
   */
  public isIOS(): boolean {
    return this.getOs() === "ios";
  }

  /**
   * ノッチがあるか
   */
  public hasNotch(): boolean {
    return this.notchDetected;
  }

  /**
   * ノッチを持っているか
   */
  private parseNotch(os: string, screen: ScreenSize, ratio: number): boolean {
    if (os !== "ios") {
      return false;
    }

    const size = this.getScreenSize(screen, ratio);

    return this.NOTCH_SIZES.some((targetSize) => {
      return targetSize[0] === size[0] && targetSize[1] === size[1];
    });
  }

  /**
   * 画面サイズを取得する
   */
  private getScreenSize(screen: ScreenSize, ratio: number): number[] {
    if (screen.width > screen.height) {
      return [screen.height * ratio, screen.width * ratio];
    }

    return [screen.width * ratio, screen.height * ratio];
  }

  /**
   * UAを解析する
   */
  private parse(userAgent: string): DeviceInfo {
    const parser = new UAParser(userAgent);
    const data = parser.getResult();

    return {
      os: this.parseOs(data.os?.name?.toLocaleLowerCase() || ""),
      osVersion: data.os?.version?.toLocaleLowerCase() || "",
      browser: this.parseBrowser(data.browser?.name?.toLocaleLowerCase() || ""),
    };
  }

  /**
   * OS名を解析する
   */
  private parseOs(os: string): string {
    if (os === "mac os") {
      return "mac";
    }

    return os;
  }

  /**
   * ブラウザ名を解析する
   */
  private parseBrowser(browser: string): string {
    if (browser === "mozilla") {
      return "firefox";
    }

    if (browser === "mobile safari") {
      return "safari";
    }

    return browser;
  }
}
